import { Button, ButtonProps } from "../../Button";
import { useCalendar } from "../CalendarContext";
import { forwardRef } from "react";
import IconKeyboardArrowDown from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_down.svg";
import IconKeyboardArrowUp from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_up.svg";
import { DateTime } from "luxon";

export type CalendarMonthButtonProps = ButtonProps & {
  /**
   * The current selection state of the calendar.
   */
  calendarSelectionState: "day" | "month" | "year";
};

/**
 * A button that displays the current month and allows the user to select a different month.
 *
 * @param props.calendarSelectionState - The current selection state of the calendar.
 * @param props.onClick - The function to call when the button is clicked.
 * @param props - The rest of the props for the Button component.
 * @returns A button that displays the current month and allows the user to select a different month.
 */
export const CalendarMonthButton = forwardRef<
  HTMLButtonElement,
  CalendarMonthButtonProps
>((props, ref) => {
  const { calendarSelectionState, onClick, ...rest } = props;
  const { selectedMonth, locale } = useCalendar();

  const currentMonth = DateTime.fromObject({ month: selectedMonth })
    .setLocale(locale)
    .toFormat("MMM");

  return (
    <Button
      ref={ref}
      appearance="ghost"
      size="small"
      onClick={onClick}
      icon={{
        after:
          calendarSelectionState === "month"
            ? IconKeyboardArrowUp
            : IconKeyboardArrowDown,
      }}
      {...rest}
    >
      {currentMonth}
    </Button>
  );
});

CalendarMonthButton.displayName = "CalendarMonthButton";
